/**
 * IntakeStep.jsx
 * --------------
 * The first screen of the assessment.
 *
 * Renders the intake form (name, email, organisation, headcount, sector,
 * primary concern) and validates all required fields before allowing
 * the respondent to proceed.
 *
 * RESPONSIBILITIES:
 *   - Render all intake fields from INTAKE_QUESTIONS in questions.js
 *   - Validate required fields on submit
 *   - Pass completed answers up to CawoodAssessment via onComplete()
 *
 * PROPS:
 *   onComplete {function} - Called with intakeAnswers object when valid form is submitted.
 *                           Shape: { I1: "Jane Smith", I2: "jane@...", ... }
 *
 * STYLING:
 *   All styles are in cawood-assessment.css.
 *   Class names follow the ca- namespace and BEM convention.
 *
 * Connected files:
 *   - questions.js        provides INTAKE_QUESTIONS
 *   - CawoodAssessment.jsx receives onComplete callback
 *   - cawood-assessment.css provides all styles
 */

import { useState } from "react";
import { INTAKE_QUESTIONS } from "../data/questions.js";
import logo from "../assets/cawood-logo.png";

// ─── Component ────────────────────────────────────────────────────────────────

export default function IntakeStep({ onComplete }) {

  // Store each field answer by question ID
  const [answers, setAnswers] = useState({});

  // Track which fields have been touched so we only show errors after interaction
  const [touched, setTouched] = useState({});

  // ── Field update handler ───────────────────────────────────────────────────
  // For select fields, value is the selected index (integer).
  // For text/email fields, value is the string.
  function handleChange(questionId, value) {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  }

  function handleBlur(questionId) {
    setTouched((prev) => ({ ...prev, [questionId]: true }));
  }

  // ── Validation ─────────────────────────────────────────────────────────────
  // Returns an object of { questionId: errorMessage } for any invalid fields.
  function validate() {
    const errors = {};
    INTAKE_QUESTIONS.forEach((q) => {
      if (!q.required) return;
      const val = answers[q.id];
      if (val === undefined || val === "" || val === null) {
        errors[q.id] = "This field is required.";
      }
      if (q.type === "email" && val && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) {
        errors[q.id] = "Please enter a valid email address.";
      }
    });
    return errors;
  }

  // ── Submit handler ─────────────────────────────────────────────────────────
  function handleSubmit(e) {
    e.preventDefault();

    // Mark all fields as touched to surface any errors
    const allTouched = {};
    INTAKE_QUESTIONS.forEach((q) => { allTouched[q.id] = true; });
    setTouched(allTouched);

    const errors = validate();
    if (Object.keys(errors).length > 0) return; // stop if invalid

    onComplete(answers);
  }

  const errors = validate();

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="ca-assessment__step">

      {/* Header panel */}
      <div className="ca-intake__header">
        <img
          src={logo}
          alt="Cawood.io"
          className="ca-intake__logo"
        />
        <h1>Digital Maturity Assessment</h1>
        <p>
          Find out where your organisation stands across six areas of digital
          practice — and get a plain-English picture of where to focus first.
          Takes 8–12 minutes.
        </p>
      </div>

      {/* Intake form */}
      <form onSubmit={handleSubmit} noValidate>

        <div className="ca-intake__fields">
          <h2>Before we start</h2>
          <p className="ca-text--muted">
            Tell us a little about your organisation. This shapes the language
            and focus of your results.
          </p>

          {INTAKE_QUESTIONS.map((question) => (
            <div className="ca-form__group" key={question.id}>

              <label className="ca-form__label" htmlFor={question.id}>
                {question.label}
              </label>

              {/* Optional help text (used on I6) */}
              {question.helpText && (
                <span className="ca-form__help">{question.helpText}</span>
              )}

              {/* Text and email inputs */}
              {(question.type === "text" || question.type === "email") && (
                <input
                  id={question.id}
                  type={question.type}
                  className={[
                    "ca-form__input",
                    touched[question.id] && errors[question.id]
                      ? "ca-form__input--error"
                      : "",
                  ].join(" ")}
                  placeholder={question.placeholder}
                  value={answers[question.id] ?? ""}
                  onChange={(e) => handleChange(question.id, e.target.value)}
                  onBlur={() => handleBlur(question.id)}
                  autoComplete={question.type === "email" ? "email" : "off"}
                />
              )}

              {/* Select fields */}
              {question.type === "select" && (
                <div className="ca-form__select-wrapper">
                  <select
                    id={question.id}
                    className={[
                      "ca-form__select",
                      touched[question.id] && errors[question.id]
                        ? "ca-form__select--error"
                        : "",
                    ].join(" ")}
                    value={answers[question.id] ?? ""}
                    onChange={(e) =>
                      // Store the index as an integer for select fields
                      // so scoring.js can use it for domain mapping (I6)
                      handleChange(
                        question.id,
                        question.id === "I6"
                          ? parseInt(e.target.value, 10)
                          : e.target.value
                      )
                    }
                    onBlur={() => handleBlur(question.id)}
                  >
                    <option value="">Select an option</option>
                    {question.options.map((option, index) => (
                      <option
                        key={option}
                        value={question.id === "I6" ? index : option}
                      >
                        {option}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Validation error message */}
              {touched[question.id] && errors[question.id] && (
                <span className="ca-form__error-message">
                  {errors[question.id]}
                </span>
              )}

            </div>
          ))}
        </div>

        {/* Submit */}
        <div className="ca-nav ca-nav--intake">
          <p className="ca-intake__note">
            Your information is used only to personalise your results and save
            them to your record. We do not share it.
          </p>
          <button type="submit" className="ca-btn ca-btn--primary">
            Begin assessment →
          </button>
        </div>

      </form>
    </div>
  );
}
